import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, effect, inject, signal } from '@angular/core';
import { FileUploadDropzoneModel } from "../../field-components/file-upload-dropzone/file-upload-dropzone.component";
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { FileUploadDialogComponent, FileUploadDialogModel } from "../../../dialogs/file-upload-dialog/file-upload-dialog.component";
import { MatButtonModule } from '@angular/material/button';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PayerSelectComponent } from "../../field-components/payer-select/payer-select.component";
import { MatTooltipModule } from '@angular/material/tooltip';
import { FileDataStore } from '../../../store/file-data.store';
import { PayerItem } from '../../../models/payer-item';
import { UploadDateComponent } from '../../field-components/upload-date/upload-date.component';
import { FileTypeSelectComponent } from '../../field-components/file-type-select/file-type-select.component';
import { UtcDateTimeFormatter } from '../../../helpers/utc-datetime-formatter';
import { FileUploadTableComponent } from './file-upload-table/file-upload-table.component';
import { MimeTypeConstants } from '../../../helpers/mime-type-helper';
import { FileDataUploadStore } from '../../../store/file-data-upload.store';
import { MatIcon } from '@angular/material/icon';
import { AgGridAngular } from 'ag-grid-angular';
import {AgGridExportHelper} from '../../../helpers/ag-grid-export-helper';

@Component({
    selector: 'app-file-upload',
    imports: [
        MatButtonModule,
        UploadDateComponent,
        PayerSelectComponent,
        MatTooltipModule,
        FileUploadTableComponent,
        FileTypeSelectComponent,
        MatIcon,
    ],
    providers: [
        UtcDateTimeFormatter,
        FileDataUploadStore,
    ],
    templateUrl: './file-upload.component.html',
    styleUrl: './file-upload.component.scss'
})
export class FileUploadComponent implements OnInit, OnDestroy {
  form: FormGroup;
  generateEnabled: boolean = false;
  private aggridExportHelper: AgGridExportHelper;

  uploadDialogDropzoneLabel = "Drag & Drop to Add File for Upload OR";
  uploadDialogBrowseButtonLabel = "Browse for File";
  generateTooltip = 'Generate list of Uploaded Files';
  private subscriptions = new Subscription();

  @ViewChild(PayerSelectComponent) payerSelectComp?: PayerSelectComponent;
  @ViewChild(FileTypeSelectComponent) fileTypeSelectComp?: FileTypeSelectComponent;
  @ViewChild(UploadDateComponent) uploadDateComp?: UploadDateComponent;
  @ViewChild(FileUploadTableComponent) fileUploadTableComp?: FileUploadTableComponent;

  readonly store = inject(FileDataStore);
  readonly uploadStore = inject(FileDataUploadStore);
  controlsValid?: string;

  uploadDateReady = signal<boolean>(false);
  payerSelectReady = signal<boolean>(false);
  fileTypeSelectReady = signal<boolean>(false);

  // Used to prevent circular logic on initial report generation
  initalLoadComplete = signal<boolean>(false);

  constructor(
    private dialog: MatDialog,
    private fb: FormBuilder,
    private changeDetector: ChangeDetectorRef
  ) {
    this.aggridExportHelper = new AgGridExportHelper();
    this.form = this.fb.group({});
    effect(() => {
      this.generateEnabled = this.store.formValid();
    });
  }

  ngOnInit() {
    this.subscriptions.add(
      this.form.statusChanges.subscribe((s) => {
        this.changeDetector.markForCheck();
        this.controlsValid = s;
      })
    );
  }

  handleUploadDateReady() {
    this.uploadDateReady.set(true);
    void this.tryInitialLoad();
  }

  handleUploadDateValueChange(date: Date | null) {
    this.store.setThresholdDate(date);
  }

  async handlePayerFieldReady() {
    this.payerSelectReady.set(true);
    await this.onPayersFieldReady();
    await this.tryInitialLoad();
  }

  async handleFileTypeReady() {
    this.fileTypeSelectReady.set(true);
    if (this.payerSelectReady()) {
      await this.selectAllFileTypes();
    }
    await this.tryInitialLoad();
  }

  handleFileTypeValueChange(ids: number[] | null) {
    this.store.setFileTypeIds(ids);
  }

  handleUploadSubmitSuccess() { 
    setTimeout(() => {
      this.onGenerateReport();
    }, 300);
  }

  async handleUploadButtonClick() {
    const dropzoneModel = new FileUploadDropzoneModel(
      false, 
      false, 
      this.uploadDialogDropzoneLabel, 
      this.uploadDialogBrowseButtonLabel,
      [
        MimeTypeConstants.CSV.mimeType, 
        MimeTypeConstants.XLS.mimeType, 
        MimeTypeConstants.XLSX.mimeType,
        MimeTypeConstants.TXT.mimeType, 
      ]
    );
    const data = { item: dropzoneModel }

    const dialogRef = this.dialog.open(FileUploadDialogComponent, new FileUploadDialogModel(data));

    this.subscriptions.add(
      dialogRef.afterClosed().subscribe(async (result) => {
        await this.store.setSelectedPayers(this.payerSelectComp?.control.value ?? []);
        if(result) {
          this.handleUploadSubmitSuccess();
        }
      })
    );
  }

  async onPayersFieldReady() {
    setTimeout(() => {
      this.payerSelectComp?.setSelectedValues([]);
    }, 200);

    // All selected by default, so set Payers list in state to all options.
    await this.store.setSelectedPayers(this.payerSelectComp?.options ?? []);

    if (this.fileTypeSelectReady()) {
      await this.selectAllFileTypes();
    }
  }

  async handlePayerValueChange(event: PayerItem[]) {
    await this.store.setSelectedPayers(event);
    await this.selectAllFileTypes();
  }

  onGenerateReport() {
    this.store.getTableData();
  }

  onExport(agGrid: AgGridAngular) {
    this.aggridExportHelper.onExport(
      'CMSFiles', 
      'CMS Files', 
      this.store.getCmsGridQueryFilters(), 
      agGrid, 
      this.fileUploadTableComp?.excludeColumnIdsOnExport ?? []
    );
  }

  private async tryInitialLoad() {
    if (!this.uploadDateReady() || !this.payerSelectReady() || !this.fileTypeSelectReady() || this.initalLoadComplete()) {
      return;
    }

    await this.selectAllFileTypes();

    setTimeout(() => {
      this.onGenerateReport();
    }, 300);
    this.initalLoadComplete.set(true);
  }

  private async selectAllFileTypes() {
    if (!this.fileTypeSelectComp) {
      return;
    }

    await new Promise((resolve) => setTimeout(resolve, 200));
    await this.fileTypeSelectComp.setSelectedValues([]);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
